import { useMemo } from "react";
import { formatDate, formatDateTime, formatPhoneNumber } from "utils";
import { QuoteDetail } from "features/quotation/types";

interface UseQuotationHeaderDataParams {
  quotation: QuoteDetail | null;
  userType: string | null | undefined;
  tireSizeRender: string;
  hubRimDisplay: string;
  carLabel: string;
  isTireType: boolean;
}

interface HeaderInfoItem {
  id: number;
  label: string;
  value: string;
  /** Omitted for bottom row (車両・タイヤ・ハブ径) — layout handled in QuotationDetail */
  width?: any;
}

/**
 * Custom hook for quotation header data (customer info and order info)
 * Handles data formatting for both tire and wheel types
 * @param params - Object containing quotation and display configuration
 * @returns Formatted customer and order info arrays
 */
const useQuotationHeaderData = ({
  quotation,
  userType,
  tireSizeRender,
  hubRimDisplay,
  carLabel,
  isTireType,
}: UseQuotationHeaderDataParams) => {
  // Customer info for header
  const customerInfo = useMemo<HeaderInfoItem[]>(() => {
    const baseItems: HeaderInfoItem[] = [
      {
        id: 1,
        label: "見積番号",
        value: quotation?.quote_no ?? "",
        width: {
          width: {
            xs: "100%",
            md: "calc((100% - 2px) / 2)",
            lg: "calc((100% - 8px) / 5)",
            xl: "calc((100% - 4px) / 3)",
          },
        },
      },
      {
        id: 2,
        label: "お客様氏名",
        value: quotation?.customer_name ?? "",
        width: {
          width: {
            xs: "100%",
            md: "calc((100% - 2px) / 2)",
            lg: "calc((100% - 8px) / 5)",
            xl: "calc((100% - 4px) / 3)",
          },
          ...(!isTireType && { minWidth: { xs: "100%", md: 160 } }),
        },
      },
      {
        id: 3,
        label: "お客様電話番号",
        value: formatPhoneNumber(quotation?.customer_tel_no ?? "", userType ?? ""),
        width: {
          width: { xs: "100%", lg: "calc((100% - 8px) / 5)", xl: "calc((100% - 4px) / 3)" },
        },
      },
      {
        id: 4,
        label: "選択車両",
        value: carLabel,
        width: {
          width: {
            xs: "100%",
            md: "calc((100% - 2px) / 2)",
            lg: "calc((100% - 8px) / 5)",
            xl: "calc((100% - 4px) / 4 * 1.5)",
          },
        },
      },
      {
        id: 5,
        label: "タイヤサイズ",
        value: tireSizeRender,
        width: {
          width: {
            xs: "100%",
            md: "calc((100% - 2px) / 2)",
            lg: "calc((100% - 60px) / 5)",
            xl: "calc((100% - 4px) / 4 + (100% - 4px) / 8)",
          },
        },
      },
      {
        id: 6,
        label: "ハブ径",
        value: hubRimDisplay,
        width: {
          width: {
            xs: "100%",
            md: "calc((100% - 2px) / 2)",
            lg: "calc((100% - 8px) / 4)",
            xl: "calc((100% - 4px) / 4)",
          },
        },
      },
    ];

    return baseItems;
  }, [quotation, userType, tireSizeRender, hubRimDisplay, carLabel, isTireType]);

  // Order info for header
  const orderInfo = useMemo<HeaderInfoItem[]>(() => {
    if (isTireType) {
      return [
        { id: 1, label: "店舗コード", value: quotation?.store_cd ?? "" },
        { id: 2, label: "見積作成者", value: quotation?.quote_by ?? "" },
        {
          id: 3,
          label: "作成日時",
          value: quotation?.created_at ? formatDateTime(quotation.created_at ?? "") : "",
        },
        {
          id: 4,
          label: "有効期限",
          value: quotation?.exp_date ? formatDate(quotation.exp_date ?? "") : "",
        },
      ];
    }

    // Wheel type
    return [
      { id: 1, label: "店舗コード", value: quotation?.store_cd ?? "" },
      { id: 2, label: "担当者", value: quotation?.quote_by ?? "" },
      {
        id: 3,
        label: "作成日時",
        value: quotation?.created_at ? formatDateTime(quotation.created_at ?? "") : "",
      },
      {
        id: 4,
        label: "有効期限",
        value: quotation?.exp_date ? formatDate(quotation.exp_date ?? "") : "",
      },
    ];
  }, [quotation, isTireType]);

  return {
    customerInfo,
    orderInfo,
  };
};

export default useQuotationHeaderData;
